/**
 * The total, said by the period — `docs/proposal.md` §4.4, §6.5, board 13.
 *
 * Two sections, **Every month · 3 months** and **One time**, one tax line,
 * then a tinted summary and the same total. Nothing here is a second price:
 * every figure comes from `monthSplit()`, whose `total` *is*
 * `selectionTotals().total` and whose periodic figures are that number
 * divided, with the last period absorbing the cent.
 *
 * **Tax follows the work it belongs to.** The tax line sits under One time
 * because that is where tax on materials belongs; a monthly figure carries
 * tax only where its own visit has taxable lines, in which case it is already
 * inside that row's `each`. Spreading one tax figure over both halves would
 * put sales tax on mowing.
 *
 * "from $288 a month" rather than "$288 a month" is `fromPeriod`: where two
 * plans have different numbers of periods the first month holds every row and
 * later ones hold fewer, so a flat figure would be true for two months out of
 * five.
 */
import type { MonthSplit, MonthView as MonthCadence, ProposalView } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { formatDollars, formatDollarsExact } from '../../lib/format.js'
import { countWords } from './summary-rail.js'

export function MonthBreakdown({ view, month, split, phone }: {
  view: ProposalView
  month: MonthCadence
  split: MonthSplit
  /**
   * The page's own answer, never a media query.
   *
   * The builder's preview frame is 390px wide inside a 1400px window, so
   * `sm:` here would measure the wrong box and put the summary's two blocks
   * side by side in a column that cannot hold them (§13).
   */
  phone: boolean
}) {
  const named = (id: string): string => view.visits.find((one) => one.id === id)?.name ?? ''
  const words = (id: string): string => {
    const visit = view.visits.find((one) => one.id === id)
    return visit ? countWords(visit) : ''
  }

  /* "Every month · 3 months", "Every 2 weeks · 8 periods". */
  const periods = split.rows[0]?.periods ?? 0
  const heading = `Every ${month.label}`
    + (split.fromPeriod || periods === 0 ? '' : ` · ${periods} ${plural(month.unit, periods)}`)

  return (
    <div className="mt-3.5">
      {split.rows.length > 0 && (
        <>
          <Eyebrow>{heading}</Eyebrow>
          {split.rows.map((row) => (
            <Row
              key={row.visitId}
              name={named(row.visitId)}
              sub={`${words(row.visitId)}, ${formatDollars(row.inAll)} in all`}
              amount={row.each}
              per={month.label}
            />
          ))}
        </>
      )}

      {split.oneTimeRows.length > 0 && (
        <>
          <Eyebrow className="pt-3">One time</Eyebrow>
          {split.oneTimeRows.map((row) => (
            <Row
              key={row.visitId}
              name={named(row.visitId)}
              sub={words(row.visitId)}
              amount={row.amount}
            />
          ))}
        </>
      )}

      {/*
        * One tax line, where the tax is. No rate means no line at all (§1.4):
        * a "$0" estimate beside a signature is a figure somebody acts on.
        */}
      {view.tax.rate > 0 && split.oneTimeTax > 0 && (
        <div className="flex items-start justify-between gap-3.5 border-t border-line-faint
                        py-3 text-[14.5px] text-text-body">
          <span className="flex flex-col gap-px">
            {view.tax.label} (estimated)
            <span className="text-[12.5px] text-text-faint">Confirmed on each invoice.</span>
          </span>
          <span className="tnum font-semibold">{formatDollarsExact(split.oneTimeTax)}</span>
        </div>
      )}

      <div className={cn(`mt-1.5 flex gap-3 rounded-record border border-brand-ring
                          bg-brand-t1 px-4 py-3.5`,
        phone ? 'flex-col' : 'flex-row justify-between gap-6')}>
        <span className="flex flex-col">
          <span className="text-[12px] font-semibold text-text-muted">
            {leadLabel(month, split)}
          </span>
          <b className="tnum text-[20px]">
            {formatDollars(split.perPeriod)}{' '}
            <span className="text-[13px] font-semibold text-text-body">
              / {month.label}{split.fromPeriod || periods === 0
                ? '' : ` for ${periods} ${plural(month.unit, periods)}`}
            </span>
          </b>
        </span>
        {split.oneTime > 0 && (
          <span className="flex flex-col">
            <span className="text-[12px] font-semibold text-text-muted">One time</span>
            <b className="tnum text-[20px]">{formatDollarsExact(split.oneTime)}</b>
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * The second line of the rail and the phone bar: "or $288/mo + $2,663.72
 * once" (board 13 cell 3).
 *
 * One function, because the rail and the bar print the same sentence and two
 * copies of it would be two sentences the moment one was edited. `null` where
 * there is nothing to say — a quote with no periodic work has one figure and
 * the line would be the total again.
 */
export function monthLine(month: MonthCadence, split: MonthSplit | null): string | null {
  if (!split || split.rows.length === 0) return null
  const per = `${formatDollars(split.perPeriod)}/${short(month)}`
  const lead = split.fromPeriod ? `from ${per}` : per
  return split.oneTime > 0
    ? `${lead} + ${formatDollarsExact(split.oneTime)} once`
    : lead
}

const short = (month: MonthCadence): string => (month.every === 1
  ? month.unit === 'month' ? 'mo' : month.unit === 'week' ? 'wk' : 'day'
  : month.label)

const plural = (unit: string, n: number): string => (n === 1 ? unit : `${unit}s`)

/**
 * "Monthly", "Weekly", "Every 2 weeks" — the label over the summary figure.
 *
 * `Month` was the first version, from capitalising the cadence's own word,
 * and it reads as a column header on a card whose whole argument is that this
 * is what repeats.
 */
export const adverb = (month: MonthCadence): string => (month.every === 1
  ? month.unit === 'month' ? 'Monthly' : month.unit === 'week' ? 'Weekly' : 'Daily'
  : `Every ${month.label}`)

/**
 * The word over a figure that leads with the month: "Monthly", or "From, per
 * month" where the plans run for different numbers of periods and the figure
 * is only the first period's. The summary box above, the rail and the bar all
 * say it, so it is said once.
 */
export const leadLabel = (month: MonthCadence, split: MonthSplit): string =>
  split.fromPeriod ? `From, per ${month.label}` : adverb(month)

function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`pt-1 text-[11.5px] font-bold uppercase tracking-[0.09em]
                     text-text-faint ${className ?? ''}`}>
      {children}
    </div>
  )
}

function Row({ name, sub, amount, per }: {
  name: string
  sub: string
  amount: number
  /** Present on a periodic row: the figure reads "$288.00 / month". */
  per?: string
}) {
  return (
    <div className="flex items-start justify-between gap-3.5 border-t border-line-faint py-3">
      <span className="flex min-w-0 flex-col gap-px">
        <span className="text-[15.5px] font-semibold text-text">{name}</span>
        {sub && <span className="tnum text-[13px] text-text-muted">{sub}</span>}
      </span>
      <span className="flex shrink-0 items-baseline gap-1 whitespace-nowrap">
        <span className="tnum text-[15.5px] font-semibold text-text">
          {formatDollarsExact(amount)}
        </span>
        {per && <span className="text-[12.5px] text-text-muted">/ {per}</span>}
      </span>
    </div>
  )
}

/**
 * The rail's sentence under the total — board 1: "Or **$288 / month** for 3
 * months, plus **$2,663.72** for the one-time work." The same figures
 * `monthLine()` prints on the phone bar, said in full where there is room.
 */
export function MonthSentence({ month, split }: { month: MonthCadence; split: MonthSplit }) {
  if (split.rows.length === 0) return null
  const periods = split.rows[0]?.periods ?? 0
  return (
    <>
      {split.fromPeriod ? 'Or from ' : 'Or '}
      <b className="text-text-body">{`${formatDollars(split.perPeriod)} / ${month.label}`}</b>
      {!split.fromPeriod && periods > 0 && ` for ${periods} ${plural(month.unit, periods)}`}
      {split.oneTime > 0
        ? (
          <>
            {', plus '}
            <b className="text-text-body">{formatDollarsExact(split.oneTime)}</b>
            {' for the one-time work.'}
          </>
          )
        : '.'}
    </>
  )
}

/**
 * The rail's sentence when the **month leads** (Aaron's review, item 2.5):
 * "For 7 months. **$6,650.44** in all." — and, with work that is not billed
 * by the period, "For 3 months, plus **$2,663.72** for the one-time work.
 * **$3,527.72** in all."
 *
 * `MonthSentence` turned inside out, and the same figures: the total is
 * `split.total`, which *is* `selectionTotals().total`, so the number the
 * reader is told in the second line is the number in the total card.
 */
export function TotalSentence({ month, split }: { month: MonthCadence; split: MonthSplit }) {
  const periods = split.rows[0]?.periods ?? 0
  const span = !split.fromPeriod && periods > 0
    ? `For ${periods} ${plural(month.unit, periods)}` : null
  return (
    <>
      {span}
      {split.oneTime > 0
        ? (
          <>
            {span ? ', plus ' : 'Plus '}
            <b className="text-text-body">{formatDollarsExact(split.oneTime)}</b>
            {' for the one-time work. '}
          </>
          )
        : span && '. '}
      <b className="text-text-body">{formatDollarsExact(split.total)}</b>
      {' in all.'}
    </>
  )
}

/**
 * The bar's second figure when the month leads: "or **$6,650.44** in all",
 * or "+ **$2,663.72** once · **$3,527.72** in all" where some of the work is
 * paid once. The phone bar and the iPad bar both print it — one component,
 * for the reason `monthLine` is one function.
 */
export function TotalAfter({ split }: { split: MonthSplit }) {
  return split.oneTime > 0
    ? (
      <>
        {'+ '}<b className="text-text-body">{formatDollarsExact(split.oneTime)}</b>{' once · '}
        <b className="text-text-body">{formatDollarsExact(split.total)}</b>{' in all'}
      </>
      )
    : (
      <>
        {'or '}<b className="text-text-body">{formatDollarsExact(split.total)}</b>{' in all'}
      </>
      )
}

/**
 * The part of the split that belongs to some of the visits — the Included
 * heading's own "$950 / month" (Aaron's review, item 2.5).
 *
 * Read off `monthSplit()`'s rows rather than worked out again, so the
 * heading's monthly figure is the sum of the very rows the Per month card
 * lists for those visits. `null` where none of them is billed by the period:
 * the heading then says its total as it always did, because "$0 / month" over
 * a one-off installation is a reading that does not apply to it.
 */
export function splitOf(split: MonthSplit, visitIds: string[]): {
  perPeriod: number
  fromPeriod: boolean
  periods: number
  oneTime: number
  inAll: number
} | null {
  const mine = new Set(visitIds)
  const rows = split.rows.filter((row) => mine.has(row.visitId))
  if (rows.length === 0) return null
  const cents = (amount: number): number => Math.round(amount * 100)
  const oneTimeCents = split.oneTimeRows
    .filter((row) => mine.has(row.visitId))
    .reduce((sum, row) => sum + cents(row.amount), 0)
  const periodicCents = rows.reduce((sum, row) => sum + cents(row.inAll), 0)
  return {
    perPeriod: rows.reduce((sum, row) => sum + cents(row.each), 0) / 100,
    fromPeriod: new Set(rows.map((row) => row.periods)).size > 1,
    periods: rows[0]!.periods,
    oneTime: oneTimeCents / 100,
    inAll: (periodicCents + oneTimeCents) / 100,
  }
}
